import { CardElement, ColorRank, PlayStyle } from "../types/battle"
import { AcademicLeague } from "../types/battle"
import {
  GameMechanics,
  MechanicsAffiliationElement,
  MechanicsAffiliationPhase,
  MechanicsBattleMode,
  MechanicsBonus,
  MechanicsCollectionSection,
  MechanicsElementBeat,
  MechanicsGuardianPath,
  MechanicsLeagueLore,
  MechanicsLeaguePrivileges,
  MechanicsNotImplemented,
  MechanicsQuest,
  MechanicsRpsAdvantage,
  MechanicsSchoolCoefficient,
  MechanicsShardsSection,
  MechanicsShardsRule,
  MechanicsStyleBeat,
  MechanicsTreasureSection,
  MechanicsStarfallSection,
  MechanicsDailyLoginBonusSection,
  MechanicsBankSection,
  MechanicsBankRule,
  MechanicsBankTermDeposits,
  MechanicsChampionFundSection,
  MechanicsHotelSection,
  MechanicsCardMarketSection,
  MechanicsAlmanacSection,
  MechanicsFuseSection,
  MechanicsFuseSuccessRate,
} from "../types/gameMechanics"
import { GuardianPath } from "../types/faction"
import { normalizeGuardianPath } from "../constants/guardianPaths"
import {
  AFFILIATION_ELEMENT_PASSIVE_LINES,
  AFFILIATION_ELEMENT_PASSIVE_TITLES,
  AFFILIATION_ELEMENT_TITLES,
} from "../constants/affiliationInfo"

const CARD_ELEMENTS: CardElement[] = [
  "FIRE",
  "WATER",
  "EARTH",
  "LIGHTNING",
  "WIND",
  "ICE",
]

const PLAY_STYLES: PlayStyle[] = ["ATTACK", "DEFENSE", "SPEED"]

const COLOR_RANKS: ColorRank[] = [
  "WHITE",
  "GREEN",
  "BLUE",
  "PURPLE",
  "ORANGE",
  "RED",
]

const LEAGUES: AcademicLeague[] = ["TITAN", "ATLANT"]

function pickNumber(value: unknown, fallback = 0): number {
  if (typeof value === "number" && Number.isFinite(value)) return value
  if (typeof value === "string" && value.trim()) {
    const n = Number(value.replace(",", "."))
    return Number.isFinite(n) ? n : fallback
  }
  return fallback
}

function pickString(value: unknown, fallback = ""): string {
  if (typeof value === "string") return value
  if (value != null) return String(value)
  return fallback
}

function pickBool(value: unknown, fallback = false): boolean {
  if (typeof value === "boolean") return value
  if (value === 1 || value === "1" || value === "true") return true
  if (value === 0 || value === "0" || value === "false") return false
  return fallback
}

function pickEnum<T extends string>(value: unknown, allowed: readonly T[]): T | null {
  const raw = pickString(value).toUpperCase()
  return allowed.includes(raw as T) ? (raw as T) : null
}

function pickNullableNumber(value: unknown): number | null {
  if (value == null || value === "") return null
  const n = pickNumber(value, NaN)
  return Number.isFinite(n) ? n : null
}

function normalizeBonus(raw: unknown): MechanicsBonus | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const key = pickString(obj.key)
  const label = pickString(obj.label)
  if (!key && !label) return null
  const contextsRaw = obj.contexts
  const contexts = Array.isArray(contextsRaw)
    ? contextsRaw.map((item) => pickString(item)).filter(Boolean)
    : []
  return {
    key: key || label,
    value: pickNumber(obj.value),
    label: label || key,
    unit: pickString(obj.unit, "count"),
    contexts,
    implemented: pickBool(obj.implemented, true),
  }
}

function normalizeElementBeat(raw: unknown): MechanicsElementBeat | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const element = pickEnum(obj.element, CARD_ELEMENTS)
  if (!element) return null
  const beatsRaw = obj.beats
  const beats = Array.isArray(beatsRaw)
    ? beatsRaw
        .map((item) => pickEnum(item, CARD_ELEMENTS))
        .filter((item): item is CardElement => item != null)
    : []
  return {
    element,
    title: pickString(obj.title, element),
    beats,
  }
}

function normalizeStyleBeat(raw: unknown): MechanicsStyleBeat | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const style = pickEnum(obj.style, PLAY_STYLES)
  const beats = pickEnum(obj.beats, PLAY_STYLES)
  if (!style || !beats) return null
  return { style, beats }
}

function normalizeSchoolCoefficient(raw: unknown): MechanicsSchoolCoefficient | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const multiplier = pickNumber(
    obj.multiplier ?? obj.defaultMultiplier,
    NaN
  )
  if (!Number.isFinite(multiplier)) return null
  const minGradeRaw = obj.minGrade ?? obj.min_grade
  const minGrade =
    minGradeRaw == null ? undefined : pickNumber(minGradeRaw, NaN)
  return {
    minGrade: minGrade != null && Number.isFinite(minGrade) ? minGrade : undefined,
    multiplier,
  }
}

function normalizeAffiliationPhase(raw: unknown): MechanicsAffiliationPhase | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const ranksRaw = obj.colorRanks ?? obj.color_ranks
  const colorRanks = Array.isArray(ranksRaw)
    ? ranksRaw
        .map((item) => pickEnum(item, COLOR_RANKS))
        .filter((item): item is ColorRank => item != null)
    : []
  const valuesRaw = obj.values
  const values: Record<string, number> = {}
  if (valuesRaw && typeof valuesRaw === "object" && !Array.isArray(valuesRaw)) {
    for (const [key, value] of Object.entries(valuesRaw)) {
      values[key] = pickNumber(value)
    }
  }
  return {
    phase: pickString(obj.phase, "early"),
    colorRanks,
    values,
  }
}

function normalizeAffiliationElement(raw: unknown): MechanicsAffiliationElement | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const element = pickEnum(obj.element, CARD_ELEMENTS)
  if (!element) return null
  const phasesRaw = obj.phases
  const phases = Array.isArray(phasesRaw)
    ? phasesRaw
        .map(normalizeAffiliationPhase)
        .filter((item): item is MechanicsAffiliationPhase => item != null)
    : []
  const activeRaw = obj.activeInBattleModes ?? obj.active_in_battle_modes
  const inactiveRaw = obj.inactiveInBattleModes ?? obj.inactive_in_battle_modes
  return {
    element,
    title: pickString(
      obj.title,
      AFFILIATION_ELEMENT_PASSIVE_TITLES[element] ||
        AFFILIATION_ELEMENT_TITLES[element] ||
        element
    ),
    description: pickString(
      obj.description ?? obj.desc,
      AFFILIATION_ELEMENT_PASSIVE_LINES[element] || ""
    ),
    activeInBattleModes: Array.isArray(activeRaw)
      ? activeRaw.map((item) => pickString(item)).filter(Boolean)
      : [],
    inactiveInBattleModes: Array.isArray(inactiveRaw)
      ? inactiveRaw.map((item) => pickString(item)).filter(Boolean)
      : [],
    phases,
  }
}

function normalizeGuardianPathEntry(raw: unknown): MechanicsGuardianPath | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const path = normalizeGuardianPath(obj.path ?? obj.key ?? obj.guardianPath)
  if (!path) return null
  const bonusesRaw = obj.bonuses
  const bonuses = Array.isArray(bonusesRaw)
    ? bonusesRaw
        .map(normalizeBonus)
        .filter((item): item is MechanicsBonus => item != null)
    : []
  return {
    path,
    title: pickString(obj.title),
    description: pickString(obj.description ?? obj.desc),
    bonuses,
  }
}

function normalizeBattleMode(raw: unknown): MechanicsBattleMode | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const key = pickString(obj.key)
  if (!key) return null
  return {
    key,
    title: pickString(obj.title, key),
    winCoins: pickNullableNumber(obj.winCoins ?? obj.win_coins),
    winPts: pickNullableNumber(obj.winPts ?? obj.win_pts),
    losePts: pickNullableNumber(obj.losePts ?? obj.lose_pts),
    energyCost: pickNullableNumber(obj.energyCost ?? obj.energy_cost),
    dailyRewardLimit: pickNullableNumber(
      obj.dailyRewardLimit ?? obj.daily_reward_limit
    ),
    guardianPassivesActive: pickBool(
      obj.guardianPassivesActive ?? obj.guardian_passives_active
    ),
    guardianPathCoinBonus: pickBool(
      obj.guardianPathCoinBonus ?? obj.guardian_path_coin_bonus
    ),
  }
}

function normalizeQuest(raw: unknown): MechanicsQuest | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const key = pickString(obj.key)
  const title = pickString(obj.title)
  if (!key && !title) return null
  return {
    key: key || title,
    title: title || key,
    description: pickString(obj.description ?? obj.desc),
    target: pickNumber(obj.target, 1),
    rewardCoins: pickNumber(obj.rewardCoins ?? obj.reward_coins),
    rewardChakra: pickNumber(obj.rewardChakra ?? obj.reward_chakra),
  }
}

function normalizeLeagueLore(raw: unknown): MechanicsLeagueLore | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const title = pickString(obj.title)
  if (!title) return null
  return {
    title,
    idea: pickString(obj.idea),
    colors: pickString(obj.colors),
  }
}

function normalizeLeaguePrivileges(raw: unknown): MechanicsLeaguePrivileges | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  return {
    bossPowerPercent: pickNullableNumber(
      obj.bossPowerPercent ?? obj.boss_power_percent
    ) ?? undefined,
    scanEnergyBonus: pickNullableNumber(
      obj.scanEnergyBonus ?? obj.scan_energy_bonus
    ) ?? undefined,
    crossLeagueWinPtsPercent: pickNullableNumber(
      obj.crossLeagueWinPtsPercent ?? obj.cross_league_win_pts_percent
    ) ?? undefined,
    quizRewardPercent: pickNullableNumber(
      obj.quizRewardPercent ?? obj.quiz_reward_percent
    ) ?? undefined,
    scanChakraLootWeightBonus: pickNullableNumber(
      obj.scanChakraLootWeightBonus ?? obj.scan_chakra_loot_weight_bonus
    ) ?? undefined,
    ptsLossReductionPercent: pickNullableNumber(
      obj.ptsLossReductionPercent ?? obj.pts_loss_reduction_percent
    ) ?? undefined,
  }
}

function normalizeNotImplemented(raw: unknown): MechanicsNotImplemented | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const key = pickString(obj.key)
  const description = pickString(obj.description ?? obj.desc)
  if (!key && !description) return null
  return { key: key || description, description: description || key }
}

function normalizeRpsAdvantage(raw: unknown): MechanicsRpsAdvantage | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  return {
    elementPercent: pickNumber(obj.elementPercent ?? obj.element_percent),
    stylePercent: pickNumber(obj.stylePercent ?? obj.style_percent),
  }
}

function normalizeList<T>(
  raw: unknown,
  normalizeItem: (item: unknown) => T | null
): T[] {
  if (!Array.isArray(raw)) return []
  return raw
    .map(normalizeItem)
    .filter((item): item is T => item != null)
}

function normalizeLeagueRecord<T>(
  raw: unknown,
  normalizeItem: (item: unknown) => T | null
): Partial<Record<AcademicLeague, T>> {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {}
  const result: Partial<Record<AcademicLeague, T>> = {}
  for (const league of LEAGUES) {
    const item = normalizeItem((raw as Record<string, unknown>)[league])
    if (item) result[league] = item
  }
  return result
}

function normalizeShardsRule(raw: unknown): MechanicsShardsRule | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const title = pickString(obj.title)
  const description = pickString(obj.description ?? obj.desc)
  if (!title && !description) return null
  return {
    key: pickString(obj.key, title || description),
    title: title || description,
    description: description || title,
  }
}

function normalizeShardsSection(raw: unknown): MechanicsShardsSection | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const title = pickString(obj.title)
  if (!title) return null
  const endpointsRaw =
    obj.endpoints && typeof obj.endpoints === "object" && !Array.isArray(obj.endpoints)
      ? (obj.endpoints as Record<string, unknown>)
      : {}
  const slotsByRank: Partial<Record<ColorRank, number>> = {}
  const slotsRaw = obj.slotsByRank ?? obj.slots_by_rank
  if (slotsRaw && typeof slotsRaw === "object" && !Array.isArray(slotsRaw)) {
    for (const rank of COLOR_RANKS) {
      const value = (slotsRaw as Record<string, unknown>)[rank]
      if (value != null) slotsByRank[rank] = pickNumber(value)
    }
  }
  const tierTitlesRaw = obj.tierTitles ?? obj.tier_titles
  const tierTitles: Partial<Record<"COMMON" | "RARE" | "EPIC", string>> = {}
  if (tierTitlesRaw && typeof tierTitlesRaw === "object" && !Array.isArray(tierTitlesRaw)) {
    for (const tier of ["COMMON", "RARE", "EPIC"] as const) {
      const value = (tierTitlesRaw as Record<string, unknown>)[tier]
      if (value != null) tierTitles[tier] = pickString(value)
    }
  }
  const dustRaw =
    obj.dust && typeof obj.dust === "object" && !Array.isArray(obj.dust)
      ? (obj.dust as Record<string, unknown>)
      : null
  return {
    title,
    description: pickString(obj.description ?? obj.desc),
    endpoints: {
      catalog: pickString(endpointsRaw.catalog) || undefined,
      inventory: pickString(endpointsRaw.inventory) || undefined,
      craft: pickString(endpointsRaw.craft) || undefined,
      install: pickString(endpointsRaw.install) || undefined,
      uninstall: pickString(endpointsRaw.uninstall) || undefined,
      shop: pickString(endpointsRaw.shop) || undefined,
    },
    rules: normalizeList(obj.rules, normalizeShardsRule),
    slotsByRank: Object.keys(slotsByRank).length > 0 ? slotsByRank : undefined,
    tierTitles: Object.keys(tierTitles).length > 0 ? tierTitles : undefined,
    definitions: Array.isArray(obj.definitions) ? obj.definitions : undefined,
    catalog: Array.isArray(obj.catalog) ? obj.catalog : undefined,
    dust: dustRaw
      ? {
          dailyBattleCap: pickNumber(
            dustRaw.dailyBattleCap ?? dustRaw.daily_battle_cap
          ) || undefined,
          sources: Array.isArray(dustRaw.sources)
            ? dustRaw.sources.map((item) => {
                const src =
                  item && typeof item === "object" && !Array.isArray(item)
                    ? (item as Record<string, unknown>)
                    : {}
                return {
                  key: pickString(src.key),
                  title: pickString(src.title),
                  amount: pickString(src.amount),
                }
              })
            : undefined,
          shopPacks: (() => {
            const packsRaw = dustRaw.shopPacks ?? dustRaw.shop_packs
            if (!Array.isArray(packsRaw)) return undefined
            return packsRaw.map((item) => {
              const pack =
                item && typeof item === "object" && !Array.isArray(item)
                  ? (item as Record<string, unknown>)
                  : {}
              return {
                element: pickString(pack.element),
                title: pickString(pack.title),
                amount: pickNumber(pack.amount),
                priceCoins: pickNumber(pack.priceCoins ?? pack.price_coins),
              }
            })
          })(),
        }
      : undefined,
  }
}

function normalizeTreasureSection(raw: unknown): MechanicsTreasureSection | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const title = pickString(obj.title, "Клад")
  const description = pickString(obj.description ?? obj.desc)
  const endpointsRaw =
    obj.endpoints && typeof obj.endpoints === "object" && !Array.isArray(obj.endpoints)
      ? (obj.endpoints as Record<string, unknown>)
      : {}
  const defaultsRaw =
    obj.defaults && typeof obj.defaults === "object" && !Array.isArray(obj.defaults)
      ? (obj.defaults as Record<string, unknown>)
      : null

  return {
    title,
    description,
    ownCityOnly: pickBool(obj.ownCityOnly ?? obj.own_city_only, true),
    claimsPerDay: pickNumber(obj.claimsPerDay ?? obj.claims_per_day, 1) ?? 1,
    defaults: defaultsRaw
      ? {
          windowStartHour: pickNumber(
            defaultsRaw.windowStartHour ?? defaultsRaw.window_start_hour
          ),
          windowEndHour: pickNumber(
            defaultsRaw.windowEndHour ?? defaultsRaw.window_end_hour
          ),
          activeCount: pickNumber(
            defaultsRaw.activeCount ?? defaultsRaw.active_count
          ),
          wavesPerDay: pickNumber(
            defaultsRaw.wavesPerDay ?? defaultsRaw.waves_per_day
          ),
          durationMinutes: pickNumber(
            defaultsRaw.durationMinutes ?? defaultsRaw.duration_minutes
          ),
          coinAmount: pickNumber(
            defaultsRaw.coinAmount ?? defaultsRaw.coin_amount
          ),
        }
      : undefined,
    endpoints: {
      points: pickString(endpointsRaw.points) || undefined,
      claim: pickString(endpointsRaw.claim) || undefined,
    },
  }
}

function normalizeStarfallSection(raw: unknown): MechanicsStarfallSection | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  return {
    title: pickString(obj.title, "Звездопад"),
    description: pickString(obj.description ?? obj.desc),
    window: pickString(obj.window) || undefined,
    activeCount: pickNumber(obj.activeCount ?? obj.active_count) ?? undefined,
    dustAmount: pickNumber(obj.dustAmount ?? obj.dust_amount) ?? undefined,
    ownCityOnly: pickBool(obj.ownCityOnly ?? obj.own_city_only, true),
    firstCome: pickBool(obj.firstCome ?? obj.first_come, true),
  }
}

function normalizeDailyLoginBonusSection(
  raw: unknown
): MechanicsDailyLoginBonusSection | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const endpointsRaw =
    obj.endpoints && typeof obj.endpoints === "object" && !Array.isArray(obj.endpoints)
      ? (obj.endpoints as Record<string, unknown>)
      : {}
  const scheduleRaw = obj.schedule
  const schedule = Array.isArray(scheduleRaw)
    ? scheduleRaw
        .map((item) => {
          if (!item || typeof item !== "object" || Array.isArray(item)) return null
          const row = item as Record<string, unknown>
          const day = pickNumber(row.day)
          const coins = pickNumber(row.coins)
          if (day < 1) return null
          return { day, coins }
        })
        .filter((item): item is { day: number; coins: number } => item != null)
    : undefined

  return {
    title: pickString(obj.title, "Ежедневный бонус за вход"),
    description: pickString(obj.description ?? obj.desc),
    timezone: pickString(obj.timezone, "Europe/Minsk") || undefined,
    baseCoins: pickNumber(obj.baseCoins ?? obj.base_coins, 50) || undefined,
    stepCoins: pickNumber(obj.stepCoins ?? obj.step_coins, 25) || undefined,
    maxCoins: pickNumber(obj.maxCoins ?? obj.max_coins, 300) || undefined,
    daysToMax: pickNumber(obj.daysToMax ?? obj.days_to_max, 11) || undefined,
    schedule: schedule && schedule.length > 0 ? schedule : undefined,
    endpoints: {
      status:
        pickString(
          endpointsRaw.status ?? endpointsRaw.get ?? endpointsRaw.info
        ) || "/game/daily-login",
      claim: pickString(endpointsRaw.claim) || "/game/daily-login/claim",
    },
  }
}

function normalizeCollectionSection(raw: unknown): MechanicsCollectionSection | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const sellPricesRaw =
    obj.sellPricesByRank ?? obj.sell_prices_by_rank
  const sellPricesByRank: Partial<Record<ColorRank, number>> = {}
  if (sellPricesRaw && typeof sellPricesRaw === "object" && !Array.isArray(sellPricesRaw)) {
    for (const rank of COLOR_RANKS) {
      const value = pickNumber((sellPricesRaw as Record<string, unknown>)[rank])
      if (value > 0) sellPricesByRank[rank] = value
    }
  }

  return {
    baseCapacity: pickNumber(obj.baseCapacity ?? obj.base_capacity, 30) || undefined,
    maxCapacity: pickNumber(obj.maxCapacity ?? obj.max_capacity, 50) || undefined,
    sellPricesByRank:
      Object.keys(sellPricesByRank).length > 0 ? sellPricesByRank : undefined,
    sellLevelBonus:
      pickNumber(obj.sellLevelBonus ?? obj.sell_level_bonus, 2) || undefined,
    sellEndpoint: pickString(obj.sellEndpoint ?? obj.sell_endpoint) || undefined,
    expandViaShopRewardType:
      pickString(
        obj.expandViaShopRewardType ?? obj.expand_via_shop_reward_type
      ) || undefined,
    reorderEndpoint:
      pickString(obj.reorderEndpoint ?? obj.reorder_endpoint) || undefined,
  }
}

function normalizeBankSection(raw: unknown): MechanicsBankSection | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const title = pickString(obj.title, "Банк")
  const description = pickString(obj.description ?? obj.desc)
  const note = pickString(obj.note) || undefined
  const endpointsRaw =
    obj.endpoints && typeof obj.endpoints === "object" && !Array.isArray(obj.endpoints)
      ? (obj.endpoints as Record<string, unknown>)
      : {}
  const defaultsRaw =
    obj.defaults && typeof obj.defaults === "object" && !Array.isArray(obj.defaults)
      ? (obj.defaults as Record<string, unknown>)
      : obj
  const rulesRaw = obj.rules
  const rules: MechanicsBankRule[] | undefined = Array.isArray(rulesRaw)
    ? rulesRaw
        .map((item) => {
          if (!item || typeof item !== "object" || Array.isArray(item)) return null
          const row = item as Record<string, unknown>
          const key = pickString(row.key)
          const ruleTitle = pickString(row.title)
          const ruleDescription = pickString(row.description ?? row.desc)
          if (!key && !ruleTitle) return null
          return {
            key: key || ruleTitle,
            title: ruleTitle || key,
            description: ruleDescription,
          }
        })
        .filter((item): item is MechanicsBankRule => item != null)
    : undefined

  const termRaw =
    obj.termDeposits && typeof obj.termDeposits === "object" && !Array.isArray(obj.termDeposits)
      ? (obj.termDeposits as Record<string, unknown>)
      : obj.term_deposits &&
          typeof obj.term_deposits === "object" &&
          !Array.isArray(obj.term_deposits)
        ? (obj.term_deposits as Record<string, unknown>)
        : null

  let termDeposits: MechanicsBankTermDeposits | undefined
  if (termRaw) {
    const plansRaw = termRaw.plans
    const plans = Array.isArray(plansRaw)
      ? plansRaw
          .map((item) => {
            if (!item || typeof item !== "object" || Array.isArray(item)) return null
            const p = item as Record<string, unknown>
            const termDays = pickNullableNumber(p.termDays ?? p.term_days)
            const rateBps = pickNullableNumber(p.rateBps ?? p.rate_bps)
            if (termDays == null || rateBps == null) return null
            return {
              termDays,
              rateBps,
              ratePercent:
                pickNullableNumber(p.ratePercent ?? p.rate_percent) ?? rateBps / 100,
            }
          })
          .filter((item): item is NonNullable<typeof item> => item != null)
      : []
    if (plans.length > 0) {
      termDeposits = {
        title: pickString(termRaw.title, "Срочные вклады") || undefined,
        description: pickString(termRaw.description ?? termRaw.desc) || undefined,
        plans,
        minAmount: pickNumber(termRaw.minAmount ?? termRaw.min_amount, 100),
        maxAmount: pickNumber(termRaw.maxAmount ?? termRaw.max_amount, 5000),
        maxActivePerUser: pickNumber(
          termRaw.maxActivePerUser ?? termRaw.max_active_per_user,
          3
        ),
        earlyInterestFactor: pickNumber(
          termRaw.earlyInterestFactor ?? termRaw.early_interest_factor,
          0.5
        ),
        source: pickString(termRaw.source) || undefined,
        payoutTo: pickString(termRaw.payoutTo ?? termRaw.payout_to) || undefined,
        earlyCloseAllowed: pickBool(
          termRaw.earlyCloseAllowed ?? termRaw.early_close_allowed,
          true
        ),
        claimAllowedWithFeeDebt: pickBool(
          termRaw.claimAllowedWithFeeDebt ?? termRaw.claim_allowed_with_fee_debt,
          true
        ),
        openBlockedByFeeDebt: pickBool(
          termRaw.openBlockedByFeeDebt ?? termRaw.open_blocked_by_fee_debt,
          true
        ),
        earlyBlockedByFeeDebt: pickBool(
          termRaw.earlyBlockedByFeeDebt ?? termRaw.early_blocked_by_fee_debt,
          true
        ),
        affectsMonthlyFee: pickBool(
          termRaw.affectsMonthlyFee ?? termRaw.affects_monthly_fee,
          false
        ),
      }
    }
  }

  return {
    title,
    description,
    note,
    defaults: {
      baseCapacity: pickNumber(
        defaultsRaw.baseCapacity ?? defaultsRaw.base_capacity ?? obj.baseCapacity
      ),
      upgradePriceCoins: pickNumber(
        defaultsRaw.upgradePriceCoins ??
          defaultsRaw.upgrade_price_coins ??
          obj.upgradePriceCoins
      ),
      upgradeSlots: pickNumber(
        defaultsRaw.upgradeSlots ?? defaultsRaw.upgrade_slots ?? obj.upgradeSlots
      ),
      maxUpgrades: pickNumber(
        defaultsRaw.maxUpgrades ?? defaultsRaw.max_upgrades ?? obj.maxUpgrades
      ),
      maxCapacity: pickNumber(
        defaultsRaw.maxCapacity ?? defaultsRaw.max_capacity ?? obj.maxCapacity
      ),
      monthlyFeeCoins: pickNumber(
        defaultsRaw.monthlyFeeCoins ??
          defaultsRaw.monthly_fee_coins ??
          obj.monthlyFeeCoins
      ),
    },
    termDeposits,
    endpoints: {
      points: pickString(endpointsRaw.points) || undefined,
      status: pickString(endpointsRaw.status) || undefined,
      cards: pickString(endpointsRaw.cards) || undefined,
      depositCoins:
        pickString(endpointsRaw.depositCoins ?? endpointsRaw.deposit_coins) ||
        undefined,
      withdrawCoins:
        pickString(endpointsRaw.withdrawCoins ?? endpointsRaw.withdraw_coins) ||
        undefined,
      depositCard:
        pickString(endpointsRaw.depositCard ?? endpointsRaw.deposit_card) ||
        undefined,
      withdrawCard:
        pickString(endpointsRaw.withdrawCard ?? endpointsRaw.withdraw_card) ||
        undefined,
      upgrade: pickString(endpointsRaw.upgrade) || undefined,
      payFee: pickString(endpointsRaw.payFee ?? endpointsRaw.pay_fee) || undefined,
      termDepositPlans:
        pickString(
          endpointsRaw.termDepositPlans ?? endpointsRaw.term_deposit_plans
        ) || undefined,
      termDeposits:
        pickString(endpointsRaw.termDeposits ?? endpointsRaw.term_deposits) ||
        undefined,
      openTermDeposit:
        pickString(
          endpointsRaw.openTermDeposit ?? endpointsRaw.open_term_deposit
        ) || undefined,
      claimTermDeposit:
        pickString(
          endpointsRaw.claimTermDeposit ?? endpointsRaw.claim_term_deposit
        ) || undefined,
      earlyCloseTermDeposit:
        pickString(
          endpointsRaw.earlyCloseTermDeposit ??
            endpointsRaw.early_close_term_deposit
        ) || undefined,
    },
    rules: rules && rules.length > 0 ? rules : undefined,
  }
}

function normalizeChampionFundSection(
  raw: unknown
): MechanicsChampionFundSection | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const title = pickString(obj.title, "Фонд чемпионов")
  const endpointsRaw =
    obj.endpoints && typeof obj.endpoints === "object" && !Array.isArray(obj.endpoints)
      ? (obj.endpoints as Record<string, unknown>)
      : {}
  const defaultsRaw =
    obj.defaults && typeof obj.defaults === "object" && !Array.isArray(obj.defaults)
      ? (obj.defaults as Record<string, unknown>)
      : null
  const sharesRaw = defaultsRaw?.payoutShares ?? defaultsRaw?.payout_shares
  const payoutShares = Array.isArray(sharesRaw)
    ? sharesRaw.map((value) => pickNumber(value)).filter(Number.isFinite)
    : undefined

  return {
    title,
    description: pickString(obj.description ?? obj.desc),
    defaults: defaultsRaw
      ? {
          minPayoutCoins: pickNumber(
            defaultsRaw.minPayoutCoins ?? defaultsRaw.min_payout_coins
          ),
          payoutShares,
          payoutTimezone: pickString(
            defaultsRaw.payoutTimezone ?? defaultsRaw.payout_timezone
          ) || undefined,
          payoutDayOfMonth: pickNumber(
            defaultsRaw.payoutDayOfMonth ?? defaultsRaw.payout_day_of_month
          ),
          payoutHour: pickNumber(
            defaultsRaw.payoutHour ?? defaultsRaw.payout_hour
          ),
          payoutMinute: pickNumber(
            defaultsRaw.payoutMinute ?? defaultsRaw.payout_minute
          ),
          radarWinFundCoins: pickNumber(
            defaultsRaw.radarWinFundCoins ?? defaultsRaw.radar_win_fund_coins
          ),
        }
      : undefined,
    endpoints: {
      point: pickString(endpointsRaw.point) || undefined,
      status: pickString(endpointsRaw.status) || undefined,
    },
  }
}

function normalizeHotelSection(raw: unknown): MechanicsHotelSection | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const title = pickString(obj.title, "Гостиница")
  const endpointsRaw =
    obj.endpoints && typeof obj.endpoints === "object" && !Array.isArray(obj.endpoints)
      ? (obj.endpoints as Record<string, unknown>)
      : {}
  const defaultsRaw =
    obj.defaults && typeof obj.defaults === "object" && !Array.isArray(obj.defaults)
      ? (obj.defaults as Record<string, unknown>)
      : null
  const packagesRaw = obj.packages ?? obj.rooms
  const packages = Array.isArray(packagesRaw)
    ? packagesRaw
        .map((item) => {
          if (!item || typeof item !== "object" || Array.isArray(item)) return null
          const row = item as Record<string, unknown>
          const slug = pickString(row.slug)
          if (!slug) return null
          return {
            slug,
            title: pickString(row.title, slug),
            energy: pickNumber(row.energy, 0) ?? 0,
            restMinutes: pickNumber(row.restMinutes ?? row.rest_minutes, 0) ?? 0,
            priceCoins: pickNumber(row.priceCoins ?? row.price_coins, 0) ?? 0,
            dailyLimit: pickNumber(row.dailyLimit ?? row.daily_limit, 1) ?? 1,
          }
        })
        .filter((item): item is NonNullable<typeof item> => item != null)
    : undefined

  return {
    title,
    description: pickString(obj.description ?? obj.desc),
    defaults: defaultsRaw
      ? {
          foreignPriceMultiplier: pickNumber(
            defaultsRaw.foreignPriceMultiplier ??
              defaultsRaw.foreign_price_multiplier
          ),
          radarDiscountPercent: pickNumber(
            defaultsRaw.radarDiscountPercent ??
              defaultsRaw.radar_discount_percent
          ),
          radarWinsForDiscount: pickNumber(
            defaultsRaw.radarWinsForDiscount ??
              defaultsRaw.radar_wins_for_discount
          ),
          fundContributionRate: pickNumber(
            defaultsRaw.fundContributionRate ??
              defaultsRaw.fund_contribution_rate
          ),
        }
      : undefined,
    endpoints: {
      points: pickString(endpointsRaw.points) || undefined,
      status: pickString(endpointsRaw.status) || undefined,
      checkIn: pickString(endpointsRaw.checkIn ?? endpointsRaw.check_in) || undefined,
      checkOut:
        pickString(endpointsRaw.checkOut ?? endpointsRaw.check_out) || undefined,
    },
    packages: packages && packages.length > 0 ? packages : undefined,
  }
}

function normalizeCardMarketSection(
  raw: unknown
): MechanicsCardMarketSection | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const title = pickString(obj.title, "Витрина")
  const endpointsRaw =
    obj.endpoints && typeof obj.endpoints === "object" && !Array.isArray(obj.endpoints)
      ? (obj.endpoints as Record<string, unknown>)
      : {}
  const defaultsRaw =
    obj.defaults && typeof obj.defaults === "object" && !Array.isArray(obj.defaults)
      ? (obj.defaults as Record<string, unknown>)
      : null

  return {
    title,
    description: pickString(obj.description ?? obj.desc),
    defaults: defaultsRaw
      ? {
          minGlobalPts: pickNumber(
            defaultsRaw.minGlobalPts ?? defaultsRaw.min_global_pts
          ),
          minColorRank: pickString(
            defaultsRaw.minColorRank ?? defaultsRaw.min_color_rank
          ) || undefined,
          activeLotsLimit: pickNumber(
            defaultsRaw.activeLotsLimit ?? defaultsRaw.active_lots_limit
          ),
          sellsDailyLimit: pickNumber(
            defaultsRaw.sellsDailyLimit ?? defaultsRaw.sells_daily_limit
          ),
          buysDailyLimit: pickNumber(
            defaultsRaw.buysDailyLimit ?? defaultsRaw.buys_daily_limit
          ),
          commissionPercent: pickNumber(
            defaultsRaw.commissionPercent ?? defaultsRaw.commission_percent
          ),
          commissionMinCoins: pickNumber(
            defaultsRaw.commissionMinCoins ?? defaultsRaw.commission_min_coins
          ),
          lotTtlDays: pickNumber(
            defaultsRaw.lotTtlDays ?? defaultsRaw.lot_ttl_days
          ),
          priceMultiplierMax: pickNumber(
            defaultsRaw.priceMultiplierMax ?? defaultsRaw.price_multiplier_max
          ),
        }
      : undefined,
    endpoints: {
      lots: pickString(endpointsRaw.lots) || undefined,
      mine: pickString(endpointsRaw.mine) || undefined,
      create: pickString(endpointsRaw.create) || undefined,
      cancel: pickString(endpointsRaw.cancel) || undefined,
      purchase: pickString(endpointsRaw.purchase) || undefined,
    },
  }
}

function normalizeAlmanacSection(raw: unknown): MechanicsAlmanacSection | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const endpointsRaw =
    obj.endpoints && typeof obj.endpoints === "object" && !Array.isArray(obj.endpoints)
      ? (obj.endpoints as Record<string, unknown>)
      : {}
  return {
    title: pickString(obj.title, "Великий Альманах Стихий"),
    description: pickString(obj.description ?? obj.desc),
    totalSlots: pickNumber(obj.totalSlots ?? obj.total_slots, 108),
    endpoints: {
      me: pickString(endpointsRaw.me) || undefined,
      public: pickString(endpointsRaw.public) || undefined,
    },
  }
}

function normalizeFuseSuccessRate(raw: unknown): MechanicsFuseSuccessRate | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const colorRank = pickEnum(obj.colorRank ?? obj.color_rank, COLOR_RANKS)
  if (!colorRank) return null
  const rate = pickNumber(obj.rate)
  const percentRaw = obj.percent
  const percent =
    percentRaw != null
      ? pickNumber(percentRaw)
      : Math.round(rate * 100)
  const nextRank = pickEnum(obj.nextRank ?? obj.next_rank, COLOR_RANKS)
  return {
    colorRank,
    title: pickString(obj.title, colorRank),
    rate,
    percent,
    nextRank,
    nextRankTitle:
      pickString(obj.nextRankTitle ?? obj.next_rank_title) ||
      (nextRank ? nextRank : null),
    canFuse: Boolean(
      obj.canFuse ?? obj.can_fuse ?? (rate > 0 && nextRank != null)
    ),
  }
}

function normalizeFuseSection(raw: unknown): MechanicsFuseSection | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const ratesRaw = obj.successRates ?? obj.success_rates
  const successRates = normalizeList(ratesRaw, normalizeFuseSuccessRate)
  if (successRates.length === 0) return null
  const boosterBonus = pickNumber(obj.boosterBonus ?? obj.booster_bonus, 0.1)
  return {
    title: pickString(obj.title, "Слияние карт"),
    description: pickString(obj.description ?? obj.desc),
    powerBonusRate: pickNumber(
      obj.powerBonusRate ?? obj.power_bonus_rate,
      0.4
    ),
    boosterBonus,
    boosterBonusPercent: pickNumber(
      obj.boosterBonusPercent ?? obj.booster_bonus_percent,
      Math.round(boosterBonus * 100)
    ),
    successRates,
  }
}

export function normalizeGameMechanics(raw: unknown): GameMechanics | null {
  const payload =
    raw && typeof raw === "object" && !Array.isArray(raw)
      ? ((raw as Record<string, unknown>).data ??
          (raw as Record<string, unknown>).result ??
          raw)
      : null
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return null
  }
  const obj = payload as Record<string, unknown>

  const combatRaw =
    obj.combat && typeof obj.combat === "object" && !Array.isArray(obj.combat)
      ? (obj.combat as Record<string, unknown>)
      : {}

  const rpsRaw = combatRaw.rpsAdvantageByRank ?? combatRaw.rps_advantage_by_rank
  const rpsAdvantageByRank: Partial<Record<ColorRank, MechanicsRpsAdvantage>> =
    {}
  if (rpsRaw && typeof rpsRaw === "object" && !Array.isArray(rpsRaw)) {
    for (const rank of COLOR_RANKS) {
      const item = normalizeRpsAdvantage(
        (rpsRaw as Record<string, unknown>)[rank]
      )
      if (item) rpsAdvantageByRank[rank] = item
    }
  }

  const bossRaw =
    obj.boss && typeof obj.boss === "object" && !Array.isArray(obj.boss)
      ? (obj.boss as Record<string, unknown>)
      : {}
  const leaguesRaw =
    obj.leagues && typeof obj.leagues === "object" && !Array.isArray(obj.leagues)
      ? (obj.leagues as Record<string, unknown>)
      : {}
  const academyRaw =
    obj.academy && typeof obj.academy === "object" && !Array.isArray(obj.academy)
      ? (obj.academy as Record<string, unknown>)
      : {}
  const questsRaw =
    obj.quests && typeof obj.quests === "object" && !Array.isArray(obj.quests)
      ? (obj.quests as Record<string, unknown>)
      : {}
  const factionWarRaw =
    obj.factionWar && typeof obj.factionWar === "object" && !Array.isArray(obj.factionWar)
      ? (obj.factionWar as Record<string, unknown>)
      : {}
  const minorityRaw =
    factionWarRaw.minorityBonus ?? factionWarRaw.minority_bonus

  const pathCoinBonusQuestKeys: Record<string, string> = {}
  const questKeysRaw =
    questsRaw.pathCoinBonusQuestKeys ?? questsRaw.path_coin_bonus_quest_keys
  if (questKeysRaw && typeof questKeysRaw === "object" && !Array.isArray(questKeysRaw)) {
    for (const [key, value] of Object.entries(questKeysRaw)) {
      pathCoinBonusQuestKeys[key] = pickString(value)
    }
  }

  return {
    combat: {
      elementBeats: normalizeList(combatRaw.elementBeats ?? combatRaw.element_beats, normalizeElementBeat),
      styleBeats: normalizeList(combatRaw.styleBeats ?? combatRaw.style_beats, normalizeStyleBeat),
      rpsAdvantageByRank,
      schoolCoefficient: normalizeList(
        combatRaw.schoolCoefficient ?? combatRaw.school_coefficient,
        normalizeSchoolCoefficient
      ),
      rebornMultiplier: pickNumber(
        combatRaw.rebornMultiplier ?? combatRaw.reborn_multiplier,
        1.5
      ),
    },
    affiliationElements: normalizeList(
      obj.affiliationElements ?? obj.affiliation_elements,
      normalizeAffiliationElement
    ),
    guardianPaths: normalizeList(
      obj.guardianPaths ?? obj.guardian_paths,
      normalizeGuardianPathEntry
    ),
    battleModes: normalizeList(obj.battleModes ?? obj.battle_modes, normalizeBattleMode),
    boss: {
      cardsRewardOnWin: pickNumber(
        bossRaw.cardsRewardOnWin ?? bossRaw.cards_reward_on_win,
        2
      ),
      cooldownHours: pickNumber(
        bossRaw.cooldownHours ?? bossRaw.cooldown_hours,
        24
      ),
    },
    leagues: {
      lore: normalizeLeagueRecord(leaguesRaw.lore, normalizeLeagueLore),
      privileges: normalizeLeagueRecord(
        leaguesRaw.privileges,
        normalizeLeaguePrivileges
      ),
    },
    academy: {
      dailyQuizCount: pickNumber(
        academyRaw.dailyQuizCount ?? academyRaw.daily_quiz_count,
        3
      ),
      perfectQuizBaseCharges: pickNumber(
        academyRaw.perfectQuizBaseCharges ?? academyRaw.perfect_quiz_base_charges,
        50
      ),
    },
    quests: {
      daily: normalizeList(questsRaw.daily, normalizeQuest),
      weekly: normalizeList(questsRaw.weekly, normalizeQuest),
      pathCoinBonusQuestKeys,
    },
    factionWar: {
      minorityBonus: {
        coinsMultiplier: pickNumber(
          minorityRaw &&
            typeof minorityRaw === "object" &&
            !Array.isArray(minorityRaw)
            ? (minorityRaw as Record<string, unknown>).coinsMultiplier ??
                (minorityRaw as Record<string, unknown>).coins_multiplier
            : undefined,
          1.1
        ),
        chakraMultiplier: pickNumber(
          minorityRaw &&
            typeof minorityRaw === "object" &&
            !Array.isArray(minorityRaw)
            ? (minorityRaw as Record<string, unknown>).chakraMultiplier ??
                (minorityRaw as Record<string, unknown>).chakra_multiplier
            : undefined,
          1.1
        ),
      },
    },
    shards: normalizeShardsSection(obj.shards) ?? undefined,
    treasure: normalizeTreasureSection(obj.treasure) ?? undefined,
    starfall: normalizeStarfallSection(obj.starfall) ?? undefined,
    dailyLoginBonus:
      normalizeDailyLoginBonusSection(
        obj.dailyLoginBonus ?? obj.daily_login_bonus
      ) ?? undefined,
    collection: normalizeCollectionSection(obj.collection) ?? undefined,
    bank: normalizeBankSection(obj.bank) ?? undefined,
    championFund:
      normalizeChampionFundSection(obj.championFund ?? obj.champion_fund) ??
      undefined,
    hotel: normalizeHotelSection(obj.hotel) ?? undefined,
    cardMarket:
      normalizeCardMarketSection(obj.cardMarket ?? obj.card_market) ?? undefined,
    almanac: normalizeAlmanacSection(obj.almanac) ?? undefined,
    fuse: normalizeFuseSection(obj.fuse) ?? undefined,
    notImplemented: normalizeList(
      obj.notImplemented ?? obj.not_implemented,
      normalizeNotImplemented
    ),
  }
}
